import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import type { Item } from '@/lib/schema/types';
import { itemsInOrder } from '@/lib/schema/loader';
import { useObservation } from '@/lib/state/useObservation';
import type { ItemScoreResult } from '@/lib/scoring/itemScore';
import { NFCDialog } from './NFCDialog';

export function ItemFooterBar({
  item,
  canComplete,
  isComplete,
  onComplete,
  scoreResult,
}: {
  item: Item;
  canComplete: boolean;
  isComplete: boolean;
  onComplete: () => void;
  scoreResult: ItemScoreResult;
}) {
  const { state } = useObservation();
  const all = itemsInOrder(item.instrument_id);
  const idx = all.findIndex((i) => i.id === item.id);
  const prev = all[idx - 1];
  const next = all[idx + 1];

  const hint = completeHint(scoreResult);

  return (
    <div className="border-t bg-background">
      {hint ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 border-b bg-amber-50 px-6 py-1.5 text-xs text-amber-900"
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{hint}</span>
        </div>
      ) : null}
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-1">
          <Button asChild variant="ghost" size="icon" title="Home">
            <Link to="/">
              <Home className="h-4 w-4" />
            </Link>
          </Button>
          {prev ? (
            <Button asChild variant="ghost" className="gap-2">
              <Link to={`/observation/${state.id}/items/${prev.id}`}>
                <ArrowLeft className="h-4 w-4" />
                Previous Item
              </Link>
            </Button>
          ) : null}
        </div>
        <NFCDialog item={item} />
        <div>
          {next ? (
            <Button
              variant={isComplete ? 'success' : 'default'}
              className="gap-2"
              disabled={!canComplete}
              onClick={() => {
                onComplete();
              }}
              asChild={isComplete}
            >
              {isComplete ? (
                <Link to={`/observation/${state.id}/items/${next.id}`}>
                  <CheckCircle2 className="h-4 w-4" />
                  Next Item
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <>
                  Complete
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              asChild
              className="gap-2"
              disabled={!canComplete}
              variant={isComplete ? 'success' : 'default'}
            >
              <Link to={`/observation/${state.id}/summary`}>
                Finish &amp; review
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Single-sentence nudge shown above the action bar when the observer can't
 * yet Complete, or has a reconcilable inconsistency. Returns null when
 * everything is tidy.
 */
function completeHint(result: ItemScoreResult): string | null {
  if (result.itemNa) return null;
  if (result.allIndicatorsNa) {
    return 'Every indicator is NA. Mark the item as Not Applicable if it does not apply, or answer at least one indicator.';
  }
  if (!result.fullySwept) {
    const n = result.blockingIndicatorIds.length;
    // When a rule is already decisive, blockingIds is empty but we may still
    // have unanswered indicators elsewhere (sweep for completeness).
    if (n > 0) {
      return `Answer ${n} more indicator${n === 1 ? '' : 's'} before completing.`;
    }
    return 'Answer the remaining indicators before completing.';
  }
  if (result.inconsistentIndicatorIds.length > 0) {
    const n = result.inconsistentIndicatorIds.length;
    return `Score is 1 because an L1 (inadequate) indicator was marked Yes — ${n} higher-level indicator${n === 1 ? ' is' : 's are'} also Yes. Double-check before completing; the rubric floors the score at 1 either way.`;
  }
  return null;
}
